import { useState } from "react";
import { Icons } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";
import { ProjectAvatar } from "@/workshop/components/panels/project-avatar";
import { InlineEditText } from "@/workshop/components/panels/inline-edit-text";
import { ConfirmDialog } from "@/workshop/components/panels/confirm-dialog";
import { BrowseTree } from "@/workshop/components/panels/browse-tree";
import type { BrowseProject } from "@/workshop/components/panels/browse-tree";
import { FrameListEnhanced } from "@/workshop/components/panels/frame-list-enhanced";
import type { EnhancedFrameEntry } from "@/workshop/components/panels/frame-list-enhanced";
import { SearchModal } from "@/workshop/components/panels/search-modal";
import type { SearchResult } from "@/workshop/components/panels/search-modal";
import { ColorPicker } from "@/workshop/components/ui/color-picker";
import { ProjectsSectionEnhanced } from "@/workshop/components/settings/projects-section-enhanced";
import {
  PRESET_COLORS,
  MOCK_SEARCH_RESULTS,
  MOCK_RECENT_ITEMS,
} from "@/workshop/data/mock-project-management";

// ─── Helper: Section ─────────────────────────────────────────────────────────

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3">
      <div>
        <h2 className="text-sm font-semibold text-primary">{title}</h2>
        {description && (
          <p className="mt-0.5 text-xs text-muted">{description}</p>
        )}
      </div>
      {children}
    </section>
  );
}

// ─── Mock data for this page ──────────────────────────────────────────────────

const AVATAR_NAMES = [
  { name: "Marketing", color: PRESET_COLORS[0] },
  { name: "Denker", color: PRESET_COLORS[1] },
  { name: "Design", color: PRESET_COLORS[2] },
  { name: "Research", color: PRESET_COLORS[3] },
  { name: "Analytics", color: PRESET_COLORS[4] },
  { name: "Frontend", color: PRESET_COLORS[5] },
  { name: "Backend", color: PRESET_COLORS[6] },
  { name: "Operations", color: PRESET_COLORS[7] },
];

const INITIAL_BROWSE_PROJECTS: BrowseProject[] = [
  {
    id: "p1",
    name: "Marketing",
    color: PRESET_COLORS[0],
    isPinned: true,
    expanded: true,
    spaces: [
      { id: "s1", name: "Research", active: true },
      { id: "s2", name: "Design" },
      { id: "s3", name: "Launch", isPinned: true },
      { id: "s4", name: "Development" },
    ],
  },
  {
    id: "p2",
    name: "Denker",
    color: PRESET_COLORS[1],
    expanded: false,
    spaces: [
      { id: "s5", name: "Planning" },
      { id: "s6", name: "Engineering" },
    ],
  },
  {
    id: "p3",
    name: "Internal",
    color: PRESET_COLORS[3],
    expanded: false,
    spaces: [{ id: "s7", name: "Metrics" }],
  },
];

const MOCK_ENHANCED_FRAMES: EnhancedFrameEntry[] = [
  {
    id: "f1",
    title: "Agent Chat",
    icon: Icons.MessageSquare,
    active: true,
    statusColor: "bg-accent",
  },
  {
    id: "f2",
    title: "api-handler.ts",
    icon: Icons.Code,
    isPinned: true,
    statusColor: "bg-accent",
  },
  {
    id: "f3",
    title: "Architecture Notes",
    icon: Icons.FileText,
    statusColor: "bg-muted",
  },
  {
    id: "f4",
    title: "Web Search",
    icon: Icons.Search,
    statusColor: "bg-warning",
  },
  {
    id: "f5",
    title: "Site Preview",
    icon: Icons.Globe,
    statusColor: "bg-muted",
  },
];

// Transform MOCK_SEARCH_RESULTS (array of groups) to Record<string, SearchResult[]>
const SEARCH_RESULTS_MAP: Record<string, SearchResult[]> = Object.fromEntries(
  MOCK_SEARCH_RESULTS.map((group) => [
    group.label,
    group.results.map((r) => ({
      ...r,
      breadcrumb: r.breadcrumb ?? "",
    })),
  ]),
);

const RECENT_ITEMS_LIST: SearchResult[] = MOCK_RECENT_ITEMS.map((r) => ({
  ...r,
  breadcrumb: r.breadcrumb ?? "",
}));

// ─── Main Page ────────────────────────────────────────────────────────────────

export function ProjectManagementPage() {
  // Inline edit state
  const [projectName, setProjectName] = useState("Marketing Website");
  const [spaceName, setSpaceName] = useState("Research");

  // Browse tree state
  const [browseProjects, setBrowseProjects] = useState(INITIAL_BROWSE_PROJECTS);

  // Confirm dialog state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [organizeOpen, setOrganizeOpen] = useState(false);

  // Search modal state
  const [searchOpen, setSearchOpen] = useState(false);

  // Color picker state
  const [pickerColor, setPickerColor] = useState<string>(PRESET_COLORS[0]);

  function handleToggleProject(projectId: string) {
    setBrowseProjects((prev) =>
      prev.map((p) =>
        p.id === projectId ? { ...p, expanded: !p.expanded } : p,
      ),
    );
  }

  return (
    <div
      className="flex flex-col gap-10 p-6"
      data-testid="project-management-page"
    >
      <div>
        <h1 className="text-lg font-semibold text-primary">
          Project Management
        </h1>
        <p className="mt-1 text-sm text-secondary">
          Showcase of all Z-122 components: avatars, inline editing, navigation,
          search, and settings.
        </p>
      </div>

      {/* 1. Project Avatar ─────────────────────────────────────────────── */}
      <Section
        title="Project Avatar"
        description="All 8 preset colors in default (24px) and sm (16px) sizes."
      >
        <div className="flex flex-col gap-3">
          <div>
            <p className="mb-2 text-[10px] text-muted">Default (24px)</p>
            <div className="flex items-center gap-2">
              {AVATAR_NAMES.map(({ name, color }) => (
                <ProjectAvatar
                  key={color}
                  name={name}
                  color={color}
                  size="default"
                  data-testid={`avatar-default-${name.toLowerCase()}`}
                />
              ))}
            </div>
          </div>
          <div>
            <p className="mb-2 text-[10px] text-muted">Small (16px)</p>
            <div className="flex items-center gap-2">
              {AVATAR_NAMES.map(({ name, color }) => (
                <ProjectAvatar
                  key={color}
                  name={name}
                  color={color}
                  size="sm"
                  data-testid={`avatar-sm-${name.toLowerCase()}`}
                />
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* 2. Inline Edit Text ──────────────────────────────────────────── */}
      <Section
        title="Inline Edit Text"
        description="Click text to edit. Enter or blur to save. Escape to cancel."
      >
        <div className="flex flex-wrap gap-8">
          <div className="flex flex-col gap-1">
            <p className="text-[10px] text-muted">Project name</p>
            <InlineEditText
              value={projectName}
              onSave={setProjectName}
              maxLength={60}
              className="text-sm font-medium"
              data-testid="inline-edit-project-name"
            />
            <p className="text-[10px] text-muted">
              Current: <span className="text-secondary">{projectName}</span>
            </p>
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-[10px] text-muted">Space name</p>
            <InlineEditText
              value={spaceName}
              onSave={setSpaceName}
              maxLength={40}
              className="text-sm font-medium"
              data-testid="inline-edit-space-name"
            />
            <p className="text-[10px] text-muted">
              Current: <span className="text-secondary">{spaceName}</span>
            </p>
          </div>
        </div>
      </Section>

      {/* 3. Header Left Island ────────────────────────────────────────── */}
      <Section
        title="Header Left Island"
        description="Glass pill with sidebar toggle, project avatar, inline-edit names, and chevron separator."
      >
        <div
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full px-2 py-1",
            "border border-glass-stroke bg-glass-fill shadow-glass backdrop-blur-glass-sm",
            "pointer-events-auto",
          )}
          data-testid="header-left-island"
        >
          <button
            type="button"
            className="flex h-6 w-6 items-center justify-center rounded-md text-muted transition-colors hover:text-primary"
            onClick={() => {}}
            data-testid="header-sidebar-toggle"
            aria-label="Toggle sidebar"
          >
            <Icons.PanelLeft className="h-3.5 w-3.5" />
          </button>
          <ProjectAvatar
            name={projectName}
            color={PRESET_COLORS[0]}
            size="sm"
            data-testid="header-project-avatar"
          />
          <InlineEditText
            value={projectName}
            onSave={setProjectName}
            className="text-xs font-medium"
            data-testid="header-project-name"
          />
          <Icons.ChevronRight
            className="h-3 w-3 shrink-0 text-muted"
            aria-hidden="true"
          />
          <InlineEditText
            value={spaceName}
            onSave={setSpaceName}
            className="text-xs text-secondary"
            data-testid="header-space-name"
          />
        </div>
      </Section>

      {/* 4. Space Switcher ────────────────────────────────────────────── */}
      <Section
        title="Space Switcher"
        description="Dot-based switcher. Active dot is larger with accent glow. Hover to see tooltip."
      >
        <SpaceSwitcherDemo />
      </Section>

      {/* 5. Browse Tab ────────────────────────────────────────────────── */}
      <Section
        title="Browse Tab"
        description="Two-level project/space tree. Right-click any item for context menu."
      >
        <div className="w-52 rounded-lg border border-glass-stroke bg-glass-fill py-1">
          <BrowseTree
            projects={browseProjects}
            onToggleProject={handleToggleProject}
            onSelectSpace={(pId, sId) =>
              console.log("[workshop] Select space", pId, sId)
            }
            onNewProject={() => console.log("[workshop] New project")}
            onRenameProject={(id) =>
              console.log("[workshop] Rename project", id)
            }
            onSetColorProject={(id) =>
              console.log("[workshop] Set color project", id)
            }
            onPinProject={(id) => console.log("[workshop] Pin project", id)}
            onProjectSettings={(id) =>
              console.log("[workshop] Project settings", id)
            }
            onNewSpace={(id) => console.log("[workshop] New space", id)}
            onDeleteProject={(id) =>
              console.log("[workshop] Delete project", id)
            }
            onRenameSpace={(pId, sId) =>
              console.log("[workshop] Rename space", pId, sId)
            }
            onSetColorSpace={(pId, sId) =>
              console.log("[workshop] Set color space", pId, sId)
            }
            onPinSpace={(pId, sId) =>
              console.log("[workshop] Pin space", pId, sId)
            }
            onOrganizeFrames={(pId, sId) =>
              console.log("[workshop] Organize frames", pId, sId)
            }
            onMoveSpace={(fromProjectId, spaceId, toProjectId) => {
              setBrowseProjects((prev) => {
                const moving = prev
                  .find((p) => p.id === fromProjectId)
                  ?.spaces.find((s) => s.id === spaceId);
                if (!moving) return prev;
                return prev.map((p) => {
                  if (p.id === fromProjectId) {
                    return {
                      ...p,
                      spaces: p.spaces.filter((s) => s.id !== spaceId),
                    };
                  }
                  if (p.id === toProjectId) {
                    return { ...p, spaces: [...p.spaces, moving] };
                  }
                  return p;
                });
              });
            }}
            onDeleteSpace={(pId, sId) =>
              console.log("[workshop] Delete space", pId, sId)
            }
          />
        </div>
      </Section>

      {/* 6. Frames Tab ────────────────────────────────────────────────── */}
      <Section
        title="Frames Tab"
        description="Enhanced frame list with pin, status dot, and context menu. Right-click any row."
      >
        <div className="w-52 rounded-lg border border-glass-stroke bg-glass-fill py-1">
          <FrameListEnhanced
            frames={MOCK_ENHANCED_FRAMES}
            onSelectFrame={(id) => console.log("[workshop] Select frame", id)}
            onRenameFrame={(id) => console.log("[workshop] Rename frame", id)}
            onSetColorFrame={(id) =>
              console.log("[workshop] Set color frame", id)
            }
            onPinFrame={(id) => console.log("[workshop] Pin frame", id)}
            onDuplicateFrame={(id) =>
              console.log("[workshop] Duplicate frame", id)
            }
            onDeleteFrame={(id) => console.log("[workshop] Delete frame", id)}
          />
        </div>
      </Section>

      {/* 7. Context Menus ─────────────────────────────────────────────── */}
      <Section title="Context Menus">
        <p className="text-xs text-secondary">
          Right-click on items in the Browse Tab or Frames Tab above to see
          context menus.
        </p>
      </Section>

      {/* 8. Confirmation Dialogs ──────────────────────────────────────── */}
      <Section
        title="Confirmation Dialogs"
        description="Destructive and non-destructive variants."
      >
        <div className="flex items-center gap-3">
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setDeleteOpen(true)}
            data-testid="open-delete-dialog-btn"
          >
            Delete project
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setOrganizeOpen(true)}
            data-testid="open-organize-dialog-btn"
          >
            Organize frames
          </Button>
        </div>
      </Section>

      {/* 9. Search Modal ──────────────────────────────────────────────── */}
      <Section
        title="Search Modal"
        description="Command palette triggered by ⌘K. Grouped results, keyboard navigation, match highlighting."
      >
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setSearchOpen(true)}
          data-testid="open-search-modal-btn"
        >
          <Icons.Search className="mr-1.5 h-3.5 w-3.5" />
          Open Search{" "}
          <kbd className="ml-1.5 rounded border border-border bg-glass-fill px-1 font-mono text-[10px] text-muted">
            ⌘K
          </kbd>
        </Button>
      </Section>

      {/* 10. Color Picker ─────────────────────────────────────────────── */}
      <Section
        title="Color Picker"
        description="Swatch-grid dropdown. Click the color tile to open."
      >
        <div className="flex items-center gap-3">
          <ColorPicker
            value={pickerColor}
            onChange={setPickerColor}
            data-testid="standalone-color-picker"
          />
          <span className="font-mono text-xs text-secondary">
            {pickerColor}
          </span>
        </div>
      </Section>

      {/* 11. Settings > Projects ──────────────────────────────────────── */}
      <Section
        title="Settings › Projects"
        description="Full projects settings section with list/detail drill-down, avatar, color picker, and spaces management."
      >
        <div className="rounded-xl border border-glass-stroke bg-glass-fill p-4">
          <ProjectsSectionEnhanced />
        </div>
      </Section>

      {/* Confirm dialogs (portaled) */}
      <ConfirmDialog
        open={deleteOpen}
        title="Delete project?"
        message="This will permanently delete the project and all its spaces and frames. This action cannot be undone."
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          console.log("[workshop] Confirmed delete");
          setDeleteOpen(false);
        }}
        onCancel={() => setDeleteOpen(false)}
        data-testid="delete-project-dialog"
      />

      <ConfirmDialog
        open={organizeOpen}
        title="Organize frames"
        message="This will reset the frame order to the default layout based on creation date. Are you sure?"
        confirmLabel="Reorganize"
        onConfirm={() => {
          console.log("[workshop] Confirmed organize");
          setOrganizeOpen(false);
        }}
        onCancel={() => setOrganizeOpen(false)}
        data-testid="organize-frames-dialog"
      />

      {/* Search modal */}
      <SearchModal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        projectName="Marketing Website"
        results={SEARCH_RESULTS_MAP}
        recentItems={RECENT_ITEMS_LIST}
        onSelect={(result) => console.log("[workshop] Search select", result)}
      />

      <div className="h-16" />
    </div>
  );
}

// ─── SpaceSwitcherDemo ────────────────────────────────────────────────────────

function SpaceSwitcherDemo() {
  const [activeId, setActiveId] = useState("s1");
  const spaces = [
    { id: "s1", label: "Research" },
    { id: "s2", label: "Design" },
    { id: "s3", label: "Development" },
    { id: "s4", label: "Launch" },
  ];

  return (
    <div
      className={cn(
        "inline-flex items-center gap-2 rounded-full px-3 py-1.5",
        "border border-glass-stroke bg-glass-fill shadow-glass backdrop-blur-glass-sm",
      )}
      role="tablist"
      aria-label="Space switcher demo"
      data-testid="space-switcher-demo"
    >
      {spaces.map((space) => {
        const isActive = space.id === activeId;
        return (
          <button
            key={space.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-label={space.label}
            onClick={() => setActiveId(space.id)}
            className="group relative flex items-center justify-center"
            data-testid={`space-dot-${space.id}`}
          >
            <span
              className={cn(
                "block rounded-full transition-all",
                isActive
                  ? "h-2.5 w-2.5 bg-accent shadow-glow-accent-sm"
                  : "h-2 w-2 bg-muted hover:bg-secondary",
              )}
            />
            <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-surface px-2 py-0.5 font-mono text-[10px] text-primary opacity-0 shadow-glass transition-opacity group-hover:opacity-100">
              {space.label}
            </span>
          </button>
        );
      })}
      <button
        type="button"
        className="flex h-4 w-4 items-center justify-center rounded-full text-muted transition-colors hover:text-primary"
        aria-label="Add space"
        onClick={() => console.log("[workshop] Add space")}
        data-testid="space-switcher-add-btn"
      >
        <Icons.Plus className="h-3 w-3" />
      </button>
    </div>
  );
}
